import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { jsonPointerGet } from '@cotera/utilities';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { asRecord, readString } from '../../utils';
import { Field } from './editor-field';
import {
  buildUpstreamSources,
  singleSamplePointerSources,
} from './variable-picker-data';
import {
  arrayOnlyUpstreamSources,
  groupByDataFromUpstreamRef,
} from './group-by-items-path';
import { UpstreamRefPickerButton } from './upstream-ref-picker';
import type { WorkflowNodeEditorProps } from './types';

const { parseGroupByConfig, isJsonPointerPath, DATASET_RUN_INPUT_META_KEY } =
  PersistedWorkflow;

/**
 * An accumulated item without a dataset trigger's run-input metadata (`__meta`:
 * `changeType`, `changedFields`, `before`, …). It rides along in each item but is
 * plumbing, never a business field to group on, so it is dropped from the key
 * picker — it stays in the item's shape, so a later step can still read it.
 */
function withoutDatasetMeta(sample: unknown): unknown {
  if (
    sample !== null &&
    typeof sample === 'object' &&
    !Array.isArray(sample) &&
    DATASET_RUN_INPUT_META_KEY in sample
  ) {
    const { [DATASET_RUN_INPUT_META_KEY]: _meta, ...rest } = sample as Record<
      string,
      unknown
    >;
    return rest;
  }
  return sample;
}

/**
 * Config editor for a group-by node: which list to split, and which field to
 * split it on.
 *
 * The second picker is the part worth getting right. Its options come from an
 * *element* of the chosen list, not from the input as a whole — so after picking
 * `/items` off a bucket flush you are offered `/user_id` and `/amount`, the
 * fields a single order actually has, rather than `/count` and `/window` which
 * belong to the envelope around them. Picking the key off the envelope is the
 * mistake this node invites, and it produces one group containing everything.
 *
 * Reports `null` until there is a key, because a group-by without one would
 * group every item under a single value: it would look like it ran and hand back
 * exactly the undivided pile it was added to break up.
 */
export const GroupByNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const data = asRecord(node.data);
  const [itemsPath, setItemsPath] = useState<string>(
    readString(data, 'itemsPath') ?? ''
  );
  const [keyPath, setKeyPath] = useState<string>(
    readString(data, 'keyPath') ?? ''
  );
  // The step whose output the list pointer reads. Undefined means the direct
  // predecessor; set, the list comes from an earlier ancestor — the same source
  // selection for-each has.
  const [sourceRunNodeId, setSourceRunNodeId] = useState<string | undefined>(
    readString(data, 'sourceRunNodeId')
  );

  // Re-seed when the drawer is pointed at a different node, matching the other
  // editors' render-phase reset rather than an effect.
  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    setItemsPath(readString(next, 'itemsPath') ?? '');
    setKeyPath(readString(next, 'keyPath') ?? '');
    setSourceRunNodeId(readString(next, 'sourceRunNodeId'));
  }

  // Every upstream step, not just the direct predecessor: a group-by can now
  // split a list produced several steps back, so the picker offers them all and
  // records which one was chosen (the same way for-each's list picker does).
  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  const directSample = useMemo(
    () =>
      upstreamSamples.find((sample) => sample.sourceDepth === 1) ??
      upstreamSamples[0],
    [upstreamSamples]
  );
  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );

  // Arrays only: a group-by splits a list, so offering non-list fields
  // (a bucket's `count`, `window`, or any scalar) is noise that invites
  // pointing at something there is nothing to group.
  const listSources = useMemo(
    () => arrayOnlyUpstreamSources(buildUpstreamSources(upstreamSamples)),
    [upstreamSamples]
  );

  /** The sample the list pointer reads: the chosen source, else the direct one. */
  const sourceSample = useMemo(() => {
    if (sourceRunNodeId !== undefined) {
      const found = upstreamSamples.find(
        (sample) => sample.sourceName === sourceRunNodeId
      );
      if (found !== undefined) {
        return found.sample;
      }
    }
    return directSample?.sample;
  }, [upstreamSamples, sourceRunNodeId, directSample]);

  /** The value the list pointer resolves to in the sample (for diagnostics). */
  const listAtPath = useMemo(() => {
    if (sourceSample === undefined) {
      return undefined;
    }
    const path = itemsPath.trim();
    return path === '' || !path.startsWith('/')
      ? sourceSample
      : jsonPointerGet(sourceSample, path);
  }, [sourceSample, itemsPath]);

  /** One element of the chosen list, which is what a key path is relative to. */
  const itemSample = useMemo(
    () => (Array.isArray(listAtPath) ? listAtPath[0] : undefined),
    [listAtPath]
  );

  const keySources = useMemo(
    () =>
      singleSamplePointerSources({
        sample: withoutDatasetMeta(itemSample),
        sourceName: 'item',
        label: 'Each item',
      }),
    [itemSample]
  );

  // Says why the "Group by" picker has nothing to offer, instead of always
  // blaming an unpicked list. Three distinct reasons, so the next move is clear:
  // pick a list, point it at an array, or wait for a sample to exist.
  const keyPickerEmptyText = useMemo(() => {
    const path = itemsPath.trim();
    if (path === '') {
      return 'Pick a list to split above first.';
    }
    if (Array.isArray(listAtPath)) {
      return 'No item fields in the sample yet. Type one, like /user_id.';
    }
    if (listAtPath !== undefined) {
      return `"${path}" is not a list. Point "List to split" at an array.`;
    }
    return `Nothing found at "${path}". Check the list path above.`;
  }, [itemsPath, listAtPath]);

  // A clearly wrong list: the path resolves to a known non-array in the sample,
  // so group-by has nothing to split. Only when the value is actually known, so
  // an opaque or not-yet-run upstream is not wrongly flagged.
  const listNotAnArray =
    sourceSample !== undefined &&
    itemsPath.trim() !== '' &&
    listAtPath !== undefined &&
    !Array.isArray(listAtPath);

  const parsed = useMemo(
    () =>
      parseGroupByConfig({
        itemsPath: itemsPath.trim(),
        keyPath: keyPath.trim(),
        ...(sourceRunNodeId !== undefined && sourceRunNodeId.trim() !== ''
          ? { sourceRunNodeId }
          : {}),
      }),
    [itemsPath, keyPath, sourceRunNodeId]
  );

  useEffect(() => {
    onChange(
      parsed.ok ? { type: 'group-by', data: { ...parsed.config } } : null
    );
  }, [parsed, onChange]);

  const onPickList = (ref: string): void => {
    const next = groupByDataFromUpstreamRef(ref);
    setItemsPath(next.itemsPath ?? '');
    // Record the source the list came from so the field reads as `bucket.items`,
    // naming which step produced it (now that the list can come from any one).
    setSourceRunNodeId(next.sourceRunNodeId);
  };

  const clearList = (): void => {
    setItemsPath('');
    setSourceRunNodeId(undefined);
  };

  // A picked list reads as `<source>.<path>` (e.g. `bucket.items`), the same
  // qualified form used elsewhere, so it names the step it came from rather than
  // a bare `/items` that could be any step's. Only when a source was picked; a
  // hand-typed pointer stays an editable box.
  const pickedSourceLabel =
    sourceRunNodeId !== undefined
      ? sourceLabels.get(sourceRunNodeId) ?? sourceRunNodeId
      : undefined;
  const dottedItemsPath = itemsPath
    .trim()
    .replace(/^\//, '')
    .split('/')
    .filter((segment) => segment !== '')
    .join('.');
  const qualifiedListLabel =
    pickedSourceLabel === undefined
      ? undefined
      : dottedItemsPath === ''
      ? pickedSourceLabel
      : `${pickedSourceLabel}.${dottedItemsPath}`;

  const listInvalid = !isJsonPointerPath(itemsPath.trim());
  const keyInvalid =
    keyPath.trim() !== '' && !isJsonPointerPath(keyPath.trim());

  return (
    <div className="flex flex-col gap-5">
      <Field
        label="List to split"
        hint="A JSON Pointer to the list, such as /items from a bucket flush. Pick it from any upstream step, or type a pointer. Blank splits the whole input, when that is itself a list."
      >
        <div className="flex items-center gap-2">
          {qualifiedListLabel !== undefined ? (
            <span className="bg-primary/10 text-primary inline-flex h-9 min-w-0 flex-1 items-center gap-1.5 rounded-md px-3 font-mono text-xs">
              <span className="truncate">{qualifiedListLabel}</span>
              <button
                type="button"
                onClick={clearList}
                className="hover:bg-primary/20 ml-auto shrink-0 rounded p-0.5"
                title="Clear list"
                aria-label="Clear list"
              >
                <X className="size-3" />
              </button>
            </span>
          ) : (
            <Input
              value={itemsPath}
              placeholder="List to split"
              aria-label="List pointer"
              aria-invalid={listInvalid}
              className="flex-1"
              onChange={(event) => {
                setItemsPath(event.target.value);
                // A hand-typed pointer has no picked source; it resolves against
                // the direct predecessor.
                setSourceRunNodeId(undefined);
              }}
            />
          )}
          <UpstreamRefPickerButton
            sources={listSources}
            onPick={onPickList}
            title={
              qualifiedListLabel !== undefined
                ? 'Change the list'
                : 'Use a list from an upstream step'
            }
            showWhenEmpty
            noFieldsText="No lists upstream yet. Type a pointer, like /items."
          />
        </div>
        {listInvalid ? (
          <Text.Caption className="block text-yellow-800">
            A pointer starts with a slash, like /items.
          </Text.Caption>
        ) : listNotAnArray ? (
          <Text.Caption className="block text-yellow-800">
            This path is not a list, so there is nothing to group. Point it at
            an array, like /items.
          </Text.Caption>
        ) : null}
      </Field>

      <Field
        label="Group by"
        hint="The field inside each item to split on. One group per distinct value."
      >
        <div className="flex items-center gap-2">
          <Input
            value={keyPath}
            placeholder="/user_id"
            aria-label="Group by field"
            aria-invalid={keyInvalid}
            className="flex-1"
            onChange={(event) => setKeyPath(event.target.value)}
          />
          <UpstreamRefPickerButton
            sources={keySources}
            onPick={(ref) => setKeyPath(ref)}
            title="Use a field from each item"
            // These are the fields of one item, not of the step before, so it
            // reads as "Each item" rather than borrowing the "Previous step"
            // heading above it, matching the bucket editor's reducer picker.
            heading="Each item"
            showWhenEmpty
            noFieldsText={keyPickerEmptyText}
          />
        </div>
        {keyInvalid ? (
          <Text.Caption className="block text-yellow-800">
            A pointer starts with a slash, like /user_id.
          </Text.Caption>
        ) : null}
      </Field>

      {/* What the next step receives. `groups` is the path a for-each fans out
          over, and nobody guesses that from two pointer inputs. */}
      {parsed.ok ? (
        <div className="border-input bg-muted/30 flex flex-col gap-2 rounded-md border p-3">
          <Text.Caption className="text-muted-foreground block">
            What this does
          </Text.Caption>
          <Text.Span className="text-foreground text-sm">
            Splits the list into one group per distinct {parsed.config.keyPath}{' '}
            value.
          </Text.Span>
          <Text.Caption className="text-muted-foreground/80 block border-t pt-2">
            Point a For each below this at <strong>groups</strong> to run the
            branch once per group. Each one carries key, items and count.
          </Text.Caption>
        </div>
      ) : (
        <Text.Caption className="text-muted-foreground/80 block">
          {parsed.error}
        </Text.Caption>
      )}
    </div>
  );
};
